<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAxios } from 'dashboard/composables/useAxios';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import VoiceAgentForm from 'dashboard/components-next/aivoice/VoiceAgentForm.vue';
import SystemPromptModal from 'dashboard/components-next/aivoice/SystemPromptModal.vue';

const router = useRouter();
const route = useRoute();
const axios = useAxios();
const { t } = useI18n();

const voiceAgent = ref(null);
const isFetching = ref(false);
const systemPromptModalRef = ref(null);

const voiceAgentId = route.params.voiceAgentId;

const fetchVoiceAgent = async () => {
  isFetching.value = true;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/:account_id/voice_agents/${voiceAgentId}`
    );
    voiceAgent.value = data;
  } catch (error) {
    console.error('Error fetching voice agent:', error);
    useAlert(t('AI_VOICE.VOICE_AGENTS.EDIT.ERROR_MESSAGE'));
  } finally {
    isFetching.value = false;
  }
};

const handleSubmit = async updatedVoiceAgent => {
  try {
    const { data } = await axios.put(
      `/api/v1/accounts/:account_id/voice_agents/${voiceAgentId}`,
      {
        voice_agent: updatedVoiceAgent,
      }
    );
    voiceAgent.value = data;
    useAlert(t('AI_VOICE.VOICE_AGENTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('AI_VOICE.VOICE_AGENTS.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const navigateToSection = section => {
  const routes = {
    flows: 'aivoice_voice_agents_flows_index',
    guardrails: 'aivoice_voice_agents_guardrails_index',
    playground: 'aivoice_voice_agents_playground',
    actions: 'aivoice_actions_index',
  };

  router.push({
    name: routes[section],
    params: section === 'actions' ? {} : { voiceAgentId },
  });
};

const openSystemPromptModal = () => {
  systemPromptModalRef.value?.dialogRef?.open();
};

const handleSystemPromptSave = async newPrompt => {
  try {
    const { data } = await axios.put(
      `/api/v1/accounts/:account_id/voice_agents/${voiceAgentId}`,
      {
        voice_agent: {
          system_prompt: newPrompt,
        },
      }
    );
    voiceAgent.value = data;
    systemPromptModalRef.value?.dialogRef?.close();
    useAlert('System prompt updated successfully');
  } catch (error) {
    const errorMessage = error?.message || 'Failed to update system prompt';
    useAlert(errorMessage);
  }
};

onMounted(() => {
  fetchVoiceAgent();
});
</script>

<template>
  <PageLayout
    :header-title="voiceAgent?.name || 'Voice Agent'"
    :show-pagination-footer="false"
    :is-fetching="isFetching"
    :show-know-more="false"
    :back-url="{ name: 'aivoice_voice_agents_index' }"
  >
    <template #body>
      <div v-if="!voiceAgent && !isFetching">
        {{ t('AI_VOICE.VOICE_AGENTS.NOT_FOUND') }}
      </div>
      <div v-else-if="voiceAgent" class="flex gap-16 w-full">
        <!-- Left Column: Settings Form -->
        <section class="flex flex-col w-full">
          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-6">
              <header class="flex flex-col items-start gap-2">
                <h2 class="text-n-slate-12 text-base font-medium">
                  Basic settings
                </h2>
                <p class="text-n-slate-11 text-sm">
                  Configure your voice agent's name, language, voice, and
                  behavior.
                </p>
              </header>

              <VoiceAgentForm
                :voice-agent="voiceAgent"
                mode="edit"
                @submit="handleSubmit"
                @cancel="
                  () => router.push({ name: 'aivoice_voice_agents_index' })
                "
              />
            </div>
          </div>
        </section>

        <!-- Right Column: The Fun Stuff -->
        <section class="flex w-full">
          <div class="flex flex-col gap-6">
            <header class="flex flex-col items-start gap-2">
              <h2 class="text-n-slate-12 text-base font-medium">
                The Fun Stuff
              </h2>
              <p class="text-n-slate-11 text-sm">
                Add more control to the voice agent with flows, guardrails, and
                testing.
              </p>
            </header>

            <div class="flex flex-col gap-6">
              <!-- System Prompt Card -->
              <div
                class="pt-3 ltr:pl-4 rtl:pr-4 ltr:pr-2 rtl:pl-2 pb-5 gap-2 flex flex-col w-full shadow outline-1 outline outline-n-container rounded-2xl bg-n-solid-2 cursor-pointer"
                @click="openSystemPromptModal"
              >
                <div class="flex items-center justify-between w-full gap-1 h-8">
                  <span
                    class="text-sm font-medium text-n-slate-12 line-clamp-1"
                  >
                    System Prompt
                  </span>
                  <div class="flex items-center gap-2">
                    <button
                      class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-6 w-6 p-0 text-xs justify-center"
                      @click.stop="openSystemPromptModal"
                    >
                      <span class="i-lucide-pen flex-shrink-0" />
                    </button>
                  </div>
                </div>

                <!-- Expandable content preview -->
                <div
                  class="relative overflow-hidden transition-all duration-300 ease-in-out"
                  style="max-height: 4rem"
                >
                  <p
                    v-if="voiceAgent?.system_prompt"
                    class="text-sm text-n-slate-11 leading-[21px] line-clamp-3"
                  >
                    {{ voiceAgent.system_prompt }}
                  </p>
                  <p
                    v-else
                    class="text-sm text-n-slate-10 italic leading-[21px]"
                  >
                    No system prompt set. Click to add one.
                  </p>
                </div>
              </div>

              <!-- Flows Card -->
              <div
                class="pt-3 ltr:pl-4 rtl:pr-4 ltr:pr-2 rtl:pl-2 pb-5 gap-2 flex flex-col w-full shadow outline-1 outline outline-n-container rounded-2xl bg-n-solid-2 cursor-pointer"
                @click="navigateToSection('flows')"
              >
                <div class="flex items-center justify-between w-full gap-1 h-8">
                  <span
                    class="text-sm font-medium text-n-slate-12 line-clamp-1"
                  >
                    Flows
                  </span>
                  <div class="flex items-center gap-2">
                    <button
                      class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-6 w-6 p-0 text-xs justify-center"
                    >
                      <span class="i-lucide-chevron-right flex-shrink-0" />
                    </button>
                  </div>
                </div>
                <span
                  class="text-n-slate-11 text-sm leading-[21px] line-clamp-5"
                >
                  Design conversation flows to define how your voice agent
                  handles different scenarios and user intents.
                </span>
              </div>

              <!-- Actions Card -->
              <div
                class="pt-3 ltr:pl-4 rtl:pr-4 ltr:pr-2 rtl:pl-2 pb-5 gap-2 flex flex-col w-full shadow outline-1 outline outline-n-container rounded-2xl bg-n-solid-2 cursor-pointer"
                @click="navigateToSection('actions')"
              >
                <div class="flex items-center justify-between w-full gap-1 h-8">
                  <span
                    class="text-sm font-medium text-n-slate-12 line-clamp-1"
                  >
                    Actions
                  </span>
                  <div class="flex items-center gap-2">
                    <button
                      class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-6 w-6 p-0 text-xs justify-center"
                    >
                      <span class="i-lucide-chevron-right flex-shrink-0" />
                    </button>
                  </div>
                </div>
                <span
                  class="text-n-slate-11 text-sm leading-[21px] line-clamp-5"
                >
                  Define API actions that your voice agent can call during
                  conversations to perform real-world tasks and integrations.
                </span>
              </div>

              <!-- Guardrails Card -->
              <div
                class="pt-3 ltr:pl-4 rtl:pr-4 ltr:pr-2 rtl:pl-2 pb-5 gap-2 flex flex-col w-full shadow outline-1 outline outline-n-container rounded-2xl bg-n-solid-2 cursor-pointer"
                @click="navigateToSection('guardrails')"
              >
                <div class="flex items-center justify-between w-full gap-1 h-8">
                  <span
                    class="text-sm font-medium text-n-slate-12 line-clamp-1"
                  >
                    Guardrails
                  </span>
                  <div class="flex items-center gap-2">
                    <button
                      class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-6 w-6 p-0 text-xs justify-center"
                    >
                      <span class="i-lucide-chevron-right flex-shrink-0" />
                    </button>
                  </div>
                </div>
                <span
                  class="text-n-slate-11 text-sm leading-[21px] line-clamp-5"
                >
                  Keeps things on track—only the kinds of questions you want
                  your agent to answer, nothing off-limits or off-topic.
                </span>
              </div>

              <!-- Playground Card -->
              <div
                class="pt-3 ltr:pl-4 rtl:pr-4 ltr:pr-2 rtl:pl-2 pb-5 gap-2 flex flex-col w-full shadow outline-1 outline outline-n-container rounded-2xl bg-n-solid-2 cursor-pointer"
                @click="navigateToSection('playground')"
              >
                <div class="flex items-center justify-between w-full gap-1 h-8">
                  <span
                    class="text-sm font-medium text-n-slate-12 line-clamp-1"
                  >
                    Playground
                  </span>
                  <div class="flex items-center gap-2">
                    <button
                      class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-6 w-6 p-0 text-xs justify-center"
                    >
                      <span class="i-lucide-chevron-right flex-shrink-0" />
                    </button>
                  </div>
                </div>
                <span
                  class="text-n-slate-11 text-sm leading-[21px] line-clamp-5"
                >
                  Test your voice agent in a sandbox environment—try different
                  scenarios, check responses, and refine before going live.
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- System Prompt Modal -->
      <SystemPromptModal
        ref="systemPromptModalRef"
        :system-prompt="voiceAgent?.system_prompt || ''"
        @save="handleSystemPromptSave"
      />
    </template>
  </PageLayout>
</template>
